import React, { useEffect, useRef, useState } from 'react';
import confetti from 'canvas-confetti';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import styled from 'styled-components';

import frlLogo from '../../../assets/images/frl/frl-logo-white.svg';
import { IBoardSnapshot } from '../../../dtos/IBoardSnapshot';
import { groupTitle } from '../logic/labels';
import { TBoardPhase } from '../logic/schedule';
import { colors } from '../styles';

export const TAKEOVER_MS = 6000;

export const BRAND_CONFETTI = [
  colors.gold,
  colors.accent,
  colors.blue,
  '#ffffff',
];

const Overlay = styled(motion.div)`
  position: absolute;
  inset: 0;
  z-index: 30;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3vh;
  padding: 1.5rem;
  text-align: center;
  background:
    radial-gradient(
      ellipse at center,
      rgba(28, 136, 227, 0.45),
      transparent 70%
    ),
    rgba(0, 18, 42, 0.96);

  @media (max-width: 768px) {
    position: fixed;
  }
`;

const Logo = styled(motion.img)`
  height: clamp(6rem, 22vh, 16rem);
  width: auto;
  max-width: 80vw;
  object-fit: contain;
`;

const Headline = styled(motion.h2)`
  margin: 0;
  font-family: 'RedHatDisplay-ExtraBold', 'RedHatDisplay-Bold';
  font-size: clamp(2rem, min(12vh, 11vw), 9rem);
  line-height: 0.95;
  letter-spacing: -0.03em;
  text-transform: uppercase;
  background: linear-gradient(180deg, #fff 30%, ${colors.accent});
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
`;

const Champions = styled(motion.ul)`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 22rem), 1fr));
  gap: 1.5vh 2vw;
  width: min(92%, 90rem);

  li {
    padding: 1.4vh 1.4vw;
    border-radius: 1rem;
    background: rgba(255, 200, 61, 0.14);
    border: 1px solid rgba(255, 200, 61, 0.55);
  }

  small {
    display: block;
    color: ${colors.gold};
    font-family: 'RedHatDisplay-Bold';
    font-size: clamp(0.8rem, 2vh, 1.4rem);
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  strong {
    display: block;
    font-family: 'RedHatDisplay-ExtraBold', 'RedHatDisplay-Bold';
    font-size: clamp(1.3rem, 4vh, 3rem);
    line-height: 1.1;
  }
`;

const COPY: Record<'live' | 'after', { kicker: string; headline: string }> = {
  live: { kicker: 'Valendo!', headline: 'O torneio começou' },
  after: { kicker: 'Fim das rodadas', headline: 'Resultado final' },
};

interface IPhaseTakeoverProps {
  phase: TBoardPhase;
  snapshot: IBoardSnapshot;
}

/**
 * Abertura em tela cheia quando o torneio muda de fase com o telão aberto:
 * "começou" (antes → ao vivo) e "resultado final" com os campeões de cada
 * grupo (ao vivo → final). Não aparece ao abrir a página já numa fase.
 */
const PhaseTakeover: React.FC<IPhaseTakeoverProps> = ({ phase, snapshot }) => {
  const reduce = useReducedMotion();
  const previous = useRef(phase);
  const [shown, setShown] = useState<'live' | 'after' | null>(null);

  useEffect(() => {
    const from = previous.current;
    previous.current = phase;
    if (from === phase || phase === 'before') return undefined;

    setShown(phase);
    const timer = setTimeout(() => setShown(null), TAKEOVER_MS);

    if (!reduce) {
      const burst = (x: number, angle: number) =>
        confetti({
          particleCount: phase === 'after' ? 160 : 90,
          angle,
          spread: 70,
          startVelocity: 60,
          origin: { x, y: 0.9 },
          colors: BRAND_CONFETTI,
          disableForReducedMotion: true,
        });
      burst(0.1, 60);
      burst(0.9, 120);
    }

    return () => clearTimeout(timer);
  }, [phase, reduce]);

  const champions =
    shown === 'after'
      ? snapshot.ranking.flatMap(group => {
          const leader = group.entries.find(
            e => e.position === 1 && e.points > 0,
          );
          return leader ? [{ group, leader }] : [];
        })
      : [];

  return (
    <AnimatePresence>
      {shown && (
        <Overlay
          key={shown}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          role="status"
          aria-live="assertive"
        >
          <Logo
            src={frlLogo}
            alt="Fuzzy Robotics League"
            initial={
              reduce ? { opacity: 0 } : { opacity: 0, scale: 0.5, y: 30 }
            }
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ type: 'spring', bounce: 0.4, duration: 0.8 }}
          />
          <motion.div
            style={{
              fontFamily: 'RedHatDisplay-Bold',
              fontSize: 'clamp(1rem, 3.4vh, 2.4rem)',
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: colors.gold,
            }}
            initial={{ opacity: 0, letterSpacing: '0.6em' }}
            animate={{ opacity: 1, letterSpacing: '0.2em' }}
            transition={{
              duration: 0.8,
              delay: 0.25,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            {COPY[shown].kicker}
          </motion.div>
          <Headline
            initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 1.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{
              type: 'spring',
              bounce: 0.3,
              duration: 0.7,
              delay: 0.4,
            }}
          >
            {COPY[shown].headline}
          </Headline>
          {champions.length > 0 && (
            <Champions
              initial="hidden"
              animate="show"
              variants={{
                show: {
                  transition: { staggerChildren: 0.15, delayChildren: 0.9 },
                },
              }}
            >
              {champions.map(({ group, leader }) => (
                <motion.li
                  key={group.tournament_modality_id + group.category}
                  variants={{
                    hidden: reduce ? { opacity: 0 } : { opacity: 0, y: 30 },
                    show: { opacity: 1, y: 0 },
                  }}
                  transition={{ type: 'spring', bounce: 0.3, duration: 0.6 }}
                >
                  <small>Campeã · {groupTitle(group)}</small>
                  <strong>{leader.team_name}</strong>
                </motion.li>
              ))}
            </Champions>
          )}
        </Overlay>
      )}
    </AnimatePresence>
  );
};

export default PhaseTakeover;
